import React from "react";
import { Box, Typography, Button, alpha } from "@mui/material";
import { MotionBox } from "@rusha/shared-theme";
import {
  contentContainerInitial,
  contentContainerInView,
  contentContainerTransition,
  heroHeadingTransition,
  heroBodyTransition,
  buttonHover,
  buttonTap,
} from "./HeroAnimations";

const APP_URL = "https://app.rusha.tierravivaai.net";

/**
 * Left column of the hero: tagline pill, H1, subhead, CTAs, social proof.
 * All text is real HTML (crawlable). One H1 per page lives here.
 */
const HeroContent: React.FC = () => (
  <MotionBox
    sx={{ position: "relative", zIndex: 2 }}
    initial={contentContainerInitial}
    whileInView={contentContainerInView}
    viewport={{ once: true }}
    transition={contentContainerTransition}
  >
    {/* Tagline pill */}
    <Box
      component="p"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        m: 0,
        mb: 3,
        px: 1.75,
        py: 0.75,
        borderRadius: 99,
        bgcolor: alpha("#10b981", 0.08),
        border: `1px solid ${alpha("#10b981", 0.3)}`,
        color: "#10b981",
        fontSize: "0.8rem",
        fontWeight: 500,
        letterSpacing: "0.01em",
      }}
    >
      <Box
        component="span"
        sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "#10b981" }}
      />
      The engineering control plane
    </Box>

    {/* H1 — single h1 per page */}
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={heroHeadingTransition}
    >
      <Typography
        component="h1"
        variant="h1"
        id="hero-title"
        sx={{
          m: 0,
          mb: 2,
          fontWeight: 800,
          fontSize: {
            xs: "2.25rem",
            sm: "2.75rem",
            md: "3.25rem",
            lg: "3.5rem",
          },
          lineHeight: 1.1,
          letterSpacing: "-0.025em",
        }}
      >
        Build. Deploy.
        <br />
        Operate.
        <br />
        <Box component="span" sx={{ color: "primary.main" }}>
          All in one platform.
        </Box>
      </Typography>
    </MotionBox>

    {/* Subhead */}
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={heroBodyTransition}
    >
      <Typography
        component="p"
        variant="body1"
        sx={{
          mb: 3,
          maxWidth: 520,
          fontSize: { xs: "1rem", md: "1.1rem" },
          color: "text.secondary",
          lineHeight: 1.6,
        }}
      >
        Unify infrastructure, templates, GitOps, AI ops, observability and
        FinOps so engineering teams can build, run and scale anywhere.
      </Typography>
    </MotionBox>

    {/* CTAs */}
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        flexWrap: { xs: "wrap", sm: "nowrap" },
        mb: 3,
      }}
    >
      <MotionBox
        whileHover={buttonHover}
        whileTap={buttonTap}
        sx={{ width: { xs: "100%", sm: "auto" } }}
      >
        <Button
          variant="contained"
          color="primary"
          size="large"
          component="a"
          href={APP_URL}
          sx={{ width: "100%", px: 3, py: 1.25, fontSize: "0.95rem" }}
        >
          Start free →
        </Button>
      </MotionBox>
      <MotionBox
        whileHover={buttonHover}
        whileTap={buttonTap}
        sx={{ width: { xs: "100%", sm: "auto" } }}
      >
        <Button
          variant="outlined"
          size="large"
          component="a"
          href="#platform"
          sx={{
            width: "100%",
            px: 3,
            py: 1.25,
            fontSize: "0.95rem",
            borderColor: "divider",
            color: "text.primary",
            "&:hover": {
              borderColor: "primary.main",
              bgcolor: alpha("#3b82f6", 0.04),
            },
          }}
        >
          Explore platform
        </Button>
      </MotionBox>
    </Box>
  </MotionBox>
);

export default HeroContent;
